import { config } from './config';

export type FormioSchema = Record<string, unknown>;
export type FormData = Record<string, unknown>;

export interface ExistingSubmission {
  submissionId: string;
  data: FormData;
  status: 'Draft' | 'Submitted';
}

export interface PublicForm {
  id: string;
  title: string;
  schema: FormioSchema;
  theme: Record<string, unknown> | null;
  consentText: string | null;
  existing: ExistingSubmission | null;
  /** Allowed parent origins for iframe embedding; null = embeddable anywhere. */
  embedAllowedOrigins: string[] | null;
  /** When true, respondents may explicitly save a draft and resume later. */
  allowSaveAndResume: boolean;
}

export interface DraftSaved {
  submissionId: string;
  draftToken: string;
  status: string;
}

export interface DraftResume {
  submissionId: string;
  data: FormData;
  status: string;
}

export interface SubmitResult {
  submissionId: string;
  status: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, method: string, body?: unknown, bearer?: string | null): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`;
  }
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let parsed: unknown;
    const text = await response.text();
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text;
    }
    throw new ApiError(response.status, response.statusText, parsed);
  }

  return (await response.json()) as T;
}

// Appends the secret-link access token (?t=…) required for SecretLink forms.
function withToken(path: string, accessToken: string | null): string {
  if (!accessToken) {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}t=${encodeURIComponent(accessToken)}`;
}

export const api = {
  getForm: (id: string, accessToken: string | null, bearer: string | null) =>
    request<PublicForm>(withToken(`/api/public/forms/${id}`, accessToken), 'GET', undefined, bearer),
  saveDraft: (id: string, data: FormData, accessToken: string | null, bearer: string | null, draftToken?: string) =>
    request<DraftSaved>(withToken(`/api/public/forms/${id}/draft`, accessToken), 'POST', { data, draftToken }, bearer),
  resume: (id: string, submissionId: string, draftToken: string, accessToken: string | null, bearer: string | null) =>
    request<DraftResume>(
      withToken(`/api/public/forms/${id}/draft/${submissionId}?token=${encodeURIComponent(draftToken)}`, accessToken),
      'GET',
      undefined,
      bearer,
    ),
  submit: (id: string, data: FormData, accessToken: string | null, bearer: string | null, draftToken?: string) =>
    request<SubmitResult>(withToken(`/api/public/forms/${id}/submit`, accessToken), 'POST', { data, draftToken }, bearer),
};

/** Extracts the missing-required-field keys from a 400 validation response, if present. */
export function missingFieldsFrom(error: unknown): string[] {
  if (error instanceof ApiError && error.body && typeof error.body === 'object') {
    const fields = (error.body as Record<string, unknown>).missingRequiredFields;
    if (Array.isArray(fields)) {
      return fields.filter((f): f is string => typeof f === 'string');
    }
  }
  return [];
}
